// frontend/src/pages/OrderPage.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { usePageLoading } from '../contexts/PageLoadingContext';
import {
    Container, Box, Typography, Paper, Alert, CircularProgress,
    Card, CardContent, CardHeader, Chip, Avatar, List, ListItem,
    ListItemAvatar, ListItemText, Divider, CardActions, Button,
    Snackbar,
    // --- NEW: Imports for the "beautiful popup" and Tooltip ---
    Dialog, DialogActions, DialogContent, Tooltip,
} from '@mui/material';
import { keyframes } from '@mui/system';
import PageAnimation from '../components/animations/PageAnimation';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EventIcon from '@mui/icons-material/Event';
import ReplayIcon from '@mui/icons-material/Replay';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import PaymentIcon from '@mui/icons-material/Payment';
import SEO from '../components/SEO';
import { customerStatusLabel, customerStatusChipColor, isInvoiceFinalized } from '../utils/orderStatus';

// const showPrice = process.env.REACT_APP_PRICE === 'true';
const showPrice = true;

// --- NEW: Keyframes for the animated popup ---
const scaleIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const drawCheck = keyframes`
  0% { stroke-dashoffset: 35; }
  100% { stroke-dashoffset: 0; }
`;

const circleFill = keyframes`
  0% { transform: scale(0); }
  100% { transform: scale(1); }
`;


// Helper to get a color for the status chip (delegates to the shared map)
const getStatusChipColor = (status) => customerStatusChipColor(status);

function OrderCard({ order, onReorder, onEdit, onCancel, onDownload, onPayNow, isProcessing, isCancelling, isDownloading, isPaying }) {
    const modeLabelMap = {
        case: 'Case(s)',
        piece: 'Piece(s)',
        bag: 'Bag(s)',
        tray: 'Tray(s)',
        weight: 'lb',
    };

    const handleDownload = () => onDownload(order);
    const isOrderActive = order.status.toLowerCase() === 'pending';
    const isInvoiceGenerated = isInvoiceFinalized(order.status);

    // Helper to determine status tooltip text
    const getStatusTooltip = (status) => {
        const s = status.toLowerCase();
        if (s === 'pending') {
            return "We are finalizing your invoice for this order. Once finalized, your order will be shipped, and invoice will be sent to you via mail.";
        }
        if (s === 'printready') {
            return "We are reviewing and finalizing the pricing for your order.";
        }
        if (s === 'processing') {
            return "Your order is being prepared and will be delivered soon.";
        }
        return "";
    };

    return (
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 3 }}>
            <CardHeader
                avatar={<Avatar sx={{ bgcolor: 'primary.main' }}><LocalShippingIcon /></Avatar>}
                title={`Order #${order.serial_no} | Placed: ${new Date(order.created_at).toLocaleDateString()}`}
                titleTypographyProps={{ fontWeight: 'bold' }}
                subheader={`Delivery Date: ${new Date(order.delivery_date).toLocaleDateString()}`}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mr: 1 }}>
                        <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary', fontWeight: 'medium' }}>
                            Status:
                        </Typography>
                        <Tooltip title={getStatusTooltip(order.status)} arrow placement="top">
                            <Chip
                                label={customerStatusLabel(order)}
                                color={getStatusChipColor(order.status)}
                                size="small"
                                sx={{ fontWeight: 'bold', mr: 1, cursor: 'help' }}
                            />
                        </Tooltip>
                        {/* --- NEW: Payment Status Chip (only once invoice is finalized/sent) --- */}
                        {isInvoiceGenerated && (
                            <Tooltip title={!order.isPaid ? "A payment statement will be sent to you for payment." : ""} arrow placement="top">
                                <Chip
                                    label={order.isPaid ? 'PAID' : 'UNPAID'}
                                    color={order.isPaid ? 'success' : 'error'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold', cursor: !order.isPaid ? 'help' : 'default' }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                }
            />
            <Divider />
            <CardContent>
                <Typography variant="h6" gutterBottom>Items</Typography>
                <List disablePadding>
                    {order.items.map((item) => {
                        const price = item.price || 0; // Price is now included in the item
                        const itemTotal = price * item.quantity;
                        return (
                            <ListItem key={`${item.product.id}-${item.mode}`} disableGutters>
                                <ListItemAvatar>
                                    <Avatar
                                        variant="rounded"
                                        src={item.product.image_url || '<https://via.placeholder.com/150>'}
                                        alt={item.product.name}
                                        sx={{ width: 56, height: 56, mr: 2 }}
                                    />
                                </ListItemAvatar>
                                <ListItemText
                                    primary={item.product.name}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                    secondary={
                                        `Qty: ${item.quantity} ${modeLabelMap[item.mode] || item.mode}` +
                                        (showPrice && isInvoiceGenerated ? ` @ $${price.toFixed(2)} each` : '')
                                    }
                                />
                                {showPrice && isInvoiceGenerated && (
                                    <Typography variant="body1" fontWeight="bold">
                                        ${itemTotal.toFixed(2)}
                                    </Typography>
                                )}
                            </ListItem>
                        );
                    })}
                </List>
                <Divider sx={{ my: 2 }} />
                {showPrice && isInvoiceGenerated && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Order Total: ${order.total_amount ? order.total_amount.toFixed(2) : 'N/A'}
                        </Typography>
                    </Box>
                )}
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'space-around', p: 1.5, bgcolor: 'grey.50', flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />} onClick={() => onReorder(order)} disabled={isProcessing || isCancelling || isPaying || order.status === 'cancelled'}>
                    {isProcessing ? 'Processing...' : 'Reorder'}
                </Button>
                <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(order)} disabled={!isOrderActive || isProcessing || isCancelling || isPaying} color="secondary">
                    Edit Order
                </Button>
                <Button size="small" startIcon={isCancelling ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />} onClick={() => onCancel(order._id)} disabled={!isOrderActive || isProcessing || isCancelling || isPaying} color="error">
                    {isCancelling ? 'Cancelling...' : 'Cancel Order'}
                </Button>
                <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={!isInvoiceGenerated || isPaying}>
                    View / Print Invoice
                </Button>
                {/* --- MODIFIED: Pay Now Button now checks 'isPaid' status --- */}
                {/* {isInvoiceGenerated && order.status.toLowerCase() !== 'cancelled' && showPrice && !order.isPaid && (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={isPaying ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
                        onClick={() => onPayNow(order)}
                        disabled={!isInvoiceGenerated || isProcessing || isCancelling || isPaying}
                        color="success"
                    >
                        {isPaying ? 'Processing...' : 'Pay Now'}
                    </Button>
                )} */}
            </CardActions>
        </Card>
    );
}


function OrderPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const { setPageLoading } = usePageLoading();

    // Report loading state to App.js for footer visibility
    useEffect(() => {
        setPageLoading(loading);
        return () => setPageLoading(false);
    }, [loading, setPageLoading]);
    const [error, setError] = useState('');

    const navigate = useNavigate();
    const [processingId, setProcessingId] = useState(null);
    const [cancellingId, setCancellingId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [payingId, setPayingId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    // --- NEW: State and Ref for the animated popup ---
    const [paymentPopupOpen, setPaymentPopupOpen] = useState(false);
    const [paymentPopupConfig, setPaymentPopupConfig] = useState({ title: 'Payment Successful!', message: 'Thank you! Your payment has been processed and confirmed.' });
    const timeoutRef = useRef(null); // Ref to hold the timeout ID for auto-closing the popup

    const fetchOrders = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }
        try {
            const response = await api.get('/orders');
            // Changed this line to sort by serial_no (highest first) instead of created_at
            const sortedOrders = response.data.sort((a, b) => b.serial_no - a.serial_no);
            setOrders(sortedOrders);
        } catch (err) {
            setError(err.response?.data?.msg || 'Please Login To View Your Orders');
            console.error("Fetch orders error:", err);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // --- NEW: Function to close the payment popup and refresh data ---
    const handleClosePaymentPopup = useCallback(() => {
        // Clear the timeout if the button is clicked before 5s
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setPaymentPopupOpen(false);
        // Refresh the order list to show the 'PAID' status without a full page loader
        fetchOrders(false);
        // Clean the payment status from the URL
        navigate('/order', { replace: true });
    }, [navigate, fetchOrders]); // Dependencies for useCallback

    // --- MODIFIED: useEffect to handle redirects from Stripe ---
    // This now shows the popup, waits 5 seconds, then closes and refreshes.
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const paymentStatus = queryParams.get('payment');

        if (paymentStatus === 'success') {
            // 1. Show the beautiful animated popup
            setPaymentPopupOpen(true);

            // 2. Set a timer to automatically close the popup after 5 seconds
            timeoutRef.current = setTimeout(() => {
                handleClosePaymentPopup();
            }, 5000);

        } else if (paymentStatus === 'cancelled') {
            // Show a simple snackbar for cancellation
            setSnackbar({ open: true, message: 'Payment was cancelled. You can try again from your order history.', severity: 'warning' });
            navigate('/order', { replace: true });
        }

        // 3. Cleanup: clear the timeout if the component unmounts
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [handleClosePaymentPopup, navigate]); // Reruns if these functions change

    const prepareCartForNavigation = async (order) => {
        setProcessingId(order._id);
        try {
            const normalItems = order.items
                .filter(item => item.product.id !== "SPECIAL_REQUEST" && item.product._id !== "SPECIAL_REQUEST")
                .map(item => ({
                    productId: item.product.id || item.product._id,
                    quantity: item.quantity,
                    mode: item.mode
                }));

            const specialItems = order.items
                .filter(item => item.product.id === "SPECIAL_REQUEST" || item.product._id === "SPECIAL_REQUEST")
                .map(item => ({
                    name: item.product.name.replace("SPECIAL REQUEST: ", ""),
                    quantity: item.quantity,
                    mode: item.mode
                }));

            const payload = {
                items: normalItems,
                special_items: specialItems,
                replace_special_items: true
            };
            await api.post('/cart', payload);
            return true;
        } catch (err) {
            const errorMessage = err.response?.data?.msg || 'Failed to prepare items. Please try again.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            console.error("Cart preparation error:", err);
            setProcessingId(null);
            return false;
        }
    };

    const handleReorder = async (orderToReorder) => {
        if (await prepareCartForNavigation(orderToReorder)) {
            navigate('/cart');
        }
    };

    const handleEditOrder = async (orderToEdit) => {
        if (await prepareCartForNavigation(orderToEdit)) {
            // Navigate to browse page with editing context so user can browse and add more items
            navigate('/browse', { state: { editingOrderId: orderToEdit._id, fromOrderEdit: true } });
        }
    };

    const handleCancelOrder = async (orderId) => {
        if (window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
            setCancellingId(orderId);
            try {
                await api.post(`/orders/${orderId}/cancel`);
                setOrders(currentOrders =>
                    currentOrders.map(o =>
                        o._id === orderId ? { ...o, status: 'cancelled' } : o
                    )
                );
                setSnackbar({ open: true, message: 'Order marked as cancelled.', severity: 'info' });
            } catch (err) {
                const errorMessage = err.response?.data?.msg || 'Failed to cancel order. Please try again.';
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                console.error("Cancel order error:", err);
            } finally {
                setCancellingId(null);
            }
        }
    };

    // --- NEW: Function to handle payment ---
    const handlePayNow = async (order) => {
        setPayingId(order._id);
        try {
            const response = await api.post(`/payment/${order.serial_no}`);
            const { checkout_url } = response.data;
            if (checkout_url) {
                // Redirect user to Stripe's payment page
                window.location.href = checkout_url;
            } else {
                throw new Error("Checkout URL not received.");
            }
        } catch (err) {
            const errorMessage = err.response?.data?.msg || 'Could not initiate payment. Please try again.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            console.error("Payment initiation error:", err);
            setPayingId(null);
        }
    };

    const handleDownloadInvoice = async (order) => {
        if (!order || !order.serial_no) {
            setSnackbar({ open: true, message: 'Could not open invoice, order data is missing.', severity: 'error' });
            return;
        }

        const token = localStorage.getItem('accessToken') || '';
        let baseURL = api.defaults.baseURL || process.env.REACT_APP_API_URL || 'https://mataxexpress.ca/api';
        if (baseURL.startsWith('/')) {
            baseURL = window.location.origin + baseURL;
        }

        const invoiceUrl = `${baseURL}/orders/${order.serial_no}/download_invoice?token=${token}`;
        window.open(invoiceUrl, '_blank');
    };

    const renderContent = () => {
        if (loading) {
            return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress size={60} /></Box>;
        }
        if (error) {
            return <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>;
        }
        if (orders.length === 0) {
            return (
                <Paper elevation={0} sx={{ textAlign: 'center', p: 4, mt: 4, bgcolor: 'grey.100', borderRadius: 3 }}>
                    <EventIcon color="action" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography variant="h5" gutterBottom>No Orders Found</Typography>
                    <Typography color="text.secondary">You haven't placed any orders yet. Visit the catalog to get started!</Typography>
                </Paper>
            );
        }
        return orders.map((order) => (
            <OrderCard
                key={order._id}
                order={order}
                onReorder={handleReorder}
                onEdit={handleEditOrder}
                onCancel={handleCancelOrder}
                onDownload={handleDownloadInvoice}
                onPayNow={handlePayNow}
                isProcessing={processingId === order._id}
                isCancelling={cancellingId === order._id}
                isDownloading={downloadingId === order._id}
                isPaying={payingId === order._id}
            />
        ));
    };

    return (
        <PageAnimation>
            <SEO
                title="Order History | Matax Express"
                description="View and manage your wholesale grocery orders. Track delivery status, download invoices, and reorder easily."
                url="https://mataxexpress.ca/order"
                noindex={true}
            />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Order History
                </Typography>
                {renderContent()}

                {/* --- NEW: The animated, interactive payment success popup --- */}
                <Dialog
                    open={paymentPopupOpen}
                    // Prevent closing by clicking outside or pressing Escape
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            borderRadius: 4,
                            p: { xs: 2, sm: 3 },
                            overflow: 'hidden',
                            textAlign: 'center',
                            // Entry animation
                            animation: `${'scaleIn'} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards`,
                        },
                    }}
                >
                    <DialogContent sx={{ p: '24px !important' }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: 'success.main', // stronger contrast for white stroke
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                                // Circle fill + scale animation (delayed)
                                transform: 'scale(0)', // Start scaled down
                                transformOrigin: 'center',
                                // define keyframes inline so they are guaranteed to exist
                                '@keyframes circleFill': {
                                    '0%': { transform: 'scale(0)', opacity: 0 },
                                    '60%': { transform: 'scale(1.05)', opacity: 1 },
                                    '100%': { transform: 'scale(1)', opacity: 1 },
                                },
                                '@keyframes drawCheck': {
                                    '0%': { strokeDashoffset: 100 },
                                    '100%': { strokeDashoffset: 0 },
                                },
                                animation: `circleFill 0.5s 0.15s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
                            }}
                        >
                            <svg
                                width="50"
                                height="50"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ display: 'block' }}
                            >
                                <path
                                    d="M5 13l4 4L19 7"
                                    stroke="#ffffff" // White checkmark
                                    strokeWidth="2.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeDasharray="100"
                                    strokeDashoffset="100"
                                    vectorEffect="non-scaling-stroke"
                                    style={{
                                        // animate the dash offset to draw the check
                                        animation: 'drawCheck 0.45s 0.45s ease-out forwards',
                                    }}
                                />
                            </svg>
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: 'success.dark' }}>
                            {paymentPopupConfig.title}
                        </Typography>

                        <Typography color="text.secondary">
                            {paymentPopupConfig.message}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', p: '0 24px 24px' }}>
                        {/* This button allows the user to close the popup before the 5s timer */}
                        <Button onClick={handleClosePaymentPopup} variant="contained" color="success" size="large">
                            View My Orders
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={12000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </PageAnimation>
    );
}

export default OrderPage;
